// Phase 999.4 Plan 07 — Step D: option overrides with ValueClass-gated input (R6, D-14).
//
// SPEC R6 acceptance: only the overridden options are POSTed — defaults stay at
// the ref_locationoptiontype.DefaultValue level. Empty input clears the override
// (CLEAR_OPTION) so the optionOverrides map only ever holds entries the operator
// deliberately set.
//
// CONTEXT D-14 / RESEARCH anti-pattern: every input is type=text, gated by
// validateByValueClass (mirrors server-side AdminValueClassParser). NO
// <input type="number"> — locale + auto-truncate + scroll-to-step issues make
// it unreliable for engineering data.
//
// Field-level help (Description) renders muted under each input so operators
// don't have to leave the wizard to look up what each option means.
//
// Rendering: options are grouped by ValueClass for readability inside a single
// scroll container (CONTEXT discretion item 2). The list lives in a fixed-max-
// height div so the wizard's Back/Next bar stays in view on shorter monitors.

import React, { useState, useEffect } from 'react';
import { Form, FormGroup, Label, Input, FormText, Alert } from 'reactstrap';
import { apiBase } from '../apiBase';
import { validateByValueClass } from '../valueClassValidator';
import { isReservedOption } from '../reservedOptions';

export function StepD_Options({ state, dispatch, auth }) {
    const [optionTypes, setOptionTypes] = useState([]);
    const [loadError, setLoadError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                let token = '';
                if (auth && typeof auth.getAccessToken === 'function') {
                    try { token = auth.getAccessToken(); } catch { /* server gate handles */ }
                }
                const res = await fetch(apiBase() + '/api/admin/option-types', {
                    headers: token ? { Authorization: 'Bearer ' + token } : {},
                });
                if (!res.ok) throw new Error('HTTP ' + res.status);
                const body = await res.json();
                if (!cancelled) setOptionTypes(Array.isArray(body) ? body : []);
            } catch (e) {
                if (!cancelled) setLoadError(e && e.message ? e.message : String(e));
            }
        })();
        return () => { cancelled = true; };
    }, [auth]);

    // Group by ValueClass for readability — alphabetical within group.
    // ASP.NET Core AddNewtonsoftJson() defaults to CamelCasePropertyNamesContractResolver,
    // so DB_LocationOptionType (Id/Name/ValueClass/...) is serialised as { id, name, valueClass, ... }.
    const groups = optionTypes.reduce((acc, o) => {
        const k = o.valueClass || 'Other';
        if (!acc[k]) acc[k] = [];
        acc[k].push(o);
        return acc;
    }, {});
    Object.values(groups).forEach((arr) => arr.sort((a, b) => (a.name || '').localeCompare(b.name || '')));

    const setValue = (otId, v) => {
        // Empty/blank → CLEAR so the override map stays minimal (R6: only overrides
        // are POSTed). We treat both '' and null as "clear" because the Input
        // component never produces null but a defensive check is cheap.
        if (v === '' || v == null) {
            dispatch({ type: 'CLEAR_OPTION', optionTypeId: otId });
        } else {
            dispatch({ type: 'SET_OPTION', optionTypeId: otId, value: v });
        }
    };

    return (
        <Form>
            <h4>Step D — Options</h4>
            <FormText>
                Override default values for any option. Leave blank to use the default (no row will be inserted).
            </FormText>
            {loadError && <Alert color="warning" className="mt-2">Failed to load option types: {loadError}</Alert>}
            <div
                style={{ maxHeight: '60vh', overflowY: 'auto', border: '1px solid #dee2e6', padding: '0.75rem', marginTop: '0.5rem' }}
                data-testid="step-d-options-list"
            >
                {Object.keys(groups).sort().map((cls) => (
                    <div key={cls}>
                        <h6 className="mt-3">{cls}</h6>
                        {groups[cls].map((o) => {
                            const reserved = isReservedOption(o.name);
                            if (reserved) {
                                return (
                                    <FormGroup key={o.id} data-testid={`step-d-option-${o.id}`}>
                                        <Label>
                                            {o.name} <small className="text-muted">({o.valueClass})</small>
                                        </Label>
                                        <Input
                                            type="text"
                                            value=""
                                            disabled
                                            placeholder="reserved — future use"
                                            data-testid={`step-d-input-${o.id}`}
                                        />
                                        <FormText className="text-muted">
                                            Reserved for a future phase — not editable from the admin portal yet.
                                        </FormText>
                                    </FormGroup>
                                );
                            }
                            const draft = state.optionOverrides[o.id] != null ? state.optionOverrides[o.id] : '';
                            // Empty draft is "no override" — never validation-failed.
                            const validation = draft === '' ? { ok: true } : validateByValueClass(draft, o.valueClass);
                            return (
                                <FormGroup key={o.id} data-testid={`step-d-option-${o.id}`}>
                                    <Label>
                                        {o.name} <small className="text-muted">({o.valueClass})</small>
                                    </Label>
                                    <Input
                                        type="text"
                                        value={draft}
                                        onChange={(e) => setValue(o.id, e.target.value)}
                                        invalid={!validation.ok}
                                        placeholder={o.defaultValue || ''}
                                        data-testid={`step-d-input-${o.id}`}
                                    />
                                    <FormText className="text-muted">{o.description || '(no description)'}</FormText>
                                    {!validation.ok && (
                                        <div className="text-danger small" data-testid={`step-d-error-${o.id}`}>
                                            {validation.error}
                                        </div>
                                    )}
                                </FormGroup>
                            );
                        })}
                    </div>
                ))}
            </div>
        </Form>
    );
}

export default StepD_Options;
